import { formatCountdown, labelForPresentation, presentationFor } from "@anthra/platform/domain"
import { isAccessDenied, toDisplayable } from "@anthra/platform/errors"
import { formatBytes } from "@anthra/platform/files"
import { Breadcrumb, BreadcrumbCurrent, BreadcrumbItem } from "@anthra/ui/breadcrumb"
import { Button } from "@anthra/ui/button"
import { buttonVariants } from "@anthra/ui/button-variants"
import { cn } from "@anthra/ui/cn"
import { downloadFile } from "@anthra/ui/download-file"
import { Skeleton } from "@anthra/ui/skeleton"
import { createFileRoute, Link } from "@tanstack/react-router"
import {
	CheckIcon,
	ClockIcon,
	DownloadIcon,
	FileTextIcon,
	ImageIcon,
	MinusIcon,
	PlusIcon,
	TriangleAlertIcon,
} from "lucide-react"
import { type ReactNode, useEffect, useRef, useState } from "react"
import { z } from "zod"

import { copy } from "@/copy/attachment-viewer"
import { usePanelAttachmentUrl } from "@/features/attachments/use-panel-attachment-url"

/**
 * Full-page attachment viewer for the panel (US-01-004.6, US-00-006.14).
 *
 * THE EXPERT'S TWIN OF THE TAXPAYER APP'S OWN VIEWER
 * (apps/web/src/routes/_app.attachments.$attachmentId.tsx) — same stage, same
 * clamped pan and zoom, same rail. It mints through
 * `/expert/attachments/{id}/link` (audited server-side) instead of the user
 * route, and its refusals speak to an Expert who has reached a record they
 * are not working rather than to the person who owns it.
 *
 * REACHED WITH THE FILE'S OWN METADATA. The mint route answers only with a
 * signed URL; the file name, type and size travel through this route's search
 * params from wherever it was already loaded — the vetting review's document
 * panes, or a query's attachment list. Opened without them (a bookmark or a
 * hand-typed url) this is the blank state, not a guess.
 *
 * A FRESH SIGNED URL PER OPEN. Nothing here caches the URL between actions.
 * The mint runs on arrival, on Renew, and on Download; each attempt lands a
 * fresh 900-second URL and the previous one is thrown away with `setState`.
 */
const searchSchema = z.object({
	fileName: z.string().optional(),
	contentType: z.string().optional(),
	sizeBytes: z.number().optional(),
	backTo: z.string().optional(),
	backLabel: z.string().optional(),
	queryId: z.string().optional(),
	vettingId: z.string().optional(),
})

export const Route = createFileRoute("/_panel/attachments/$attachmentId")({
	validateSearch: searchSchema,
	component: PanelAttachmentViewer,
})

const ZOOM_STEP = 25
const ZOOM_MIN = 100
const ZOOM_MAX = 400

function PanelAttachmentViewer() {
	const { attachmentId } = Route.useParams()
	const { fileName, contentType, sizeBytes, backTo, backLabel } = Route.useSearch()
	const mint = usePanelAttachmentUrl()
	const save = usePanelAttachmentUrl()
	const [signedUrl, setSignedUrl] = useState<string | null>(null)
	const [expiresAt, setExpiresAt] = useState<string | null>(null)
	const [ttlSeconds, setTtlSeconds] = useState(900)
	const [remainingSeconds, setRemainingSeconds] = useState(0)
	const [zoom, setZoom] = useState(100)
	const [pan, setPan] = useState({ x: 0, y: 0 })
	const [saved, setSaved] = useState(false)
	const dragRef = useRef<{
		startX: number
		startY: number
		originX: number
		originY: number
	} | null>(null)
	const stageRef = useRef<HTMLDivElement | null>(null)
	const imgRef = useRef<HTMLImageElement | null>(null)

	// Clamp pan so the image can only be pushed as far as its own edges. The
	// image's layout box (`offsetWidth`/`offsetHeight`) is its size at scale 1,
	// so scaled size is that times zoom/100 and the maximum translation per axis
	// is half the overflow — anything more would slide an edge past the stage.
	function clampPan(next: { x: number; y: number }, atZoom: number): { x: number; y: number } {
		const stage = stageRef.current
		const img = imgRef.current
		if (!stage || !img) return next
		const scale = atZoom / 100
		const overflowX = Math.max(0, img.offsetWidth * scale - stage.clientWidth)
		const overflowY = Math.max(0, img.offsetHeight * scale - stage.clientHeight)
		const maxX = overflowX / 2
		const maxY = overflowY / 2
		return {
			x: Math.max(-maxX, Math.min(maxX, next.x)),
			y: Math.max(-maxY, Math.min(maxY, next.y)),
		}
	}

	const known = fileName !== undefined && contentType !== undefined
	const presentation = known ? presentationFor(contentType, fileName) : null

	const mintMutate = mint.mutate
	useEffect(() => {
		if (!known) return
		setSignedUrl(null)
		setExpiresAt(null)
		setSaved(false)
		setZoom(100)
		setPan({ x: 0, y: 0 })
		mintMutate(
			{ attachmentId, disposition: "inline" },
			{
				onSuccess: signed => {
					setSignedUrl(signed.url)
					setExpiresAt(signed.expiresAt)
					setTtlSeconds(signed.ttlSeconds)
				},
			},
		)
	}, [attachmentId, known, mintMutate])

	useEffect(() => {
		if (!expiresAt) return
		const tick = (): void => {
			const remaining = Math.max(
				0,
				Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000),
			)
			setRemainingSeconds(remaining)
		}
		tick()
		const id = window.setInterval(tick, 1000)
		return () => {
			window.clearInterval(id)
		}
	}, [expiresAt])

	const spent = expiresAt !== null && remainingSeconds <= 0
	const trackFraction = ttlSeconds > 0 ? Math.min(1, remainingSeconds / ttlSeconds) : 0

	function renew(): void {
		setSaved(false)
		mint.mutate(
			{ attachmentId, disposition: "inline" },
			{
				onSuccess: signed => {
					setSignedUrl(signed.url)
					setExpiresAt(signed.expiresAt)
					setTtlSeconds(signed.ttlSeconds)
				},
			},
		)
	}

	function download(): void {
		if (!fileName) return
		save.mutate(
			{ attachmentId, disposition: "attachment" },
			{
				onSuccess: signed => {
					downloadFile(signed.url, fileName)
					setSaved(true)
				},
			},
		)
	}

	if (!known) {
		return (
			<div
				className="flex flex-col gap-4"
				data-screen="attachment-viewer"
			>
				<div className="flex flex-col items-center gap-3 py-10 text-center">
					<p
						className="bg-subtle text-primary rounded-circle flex size-14 items-center justify-center"
						aria-hidden="true"
					>
						<FileTextIcon
							className="size-7 shrink-0"
							aria-hidden="true"
						/>
					</p>
					<h2 className="text-card-title text-foreground">{copy.empty.title}</h2>
					<p className="text-description text-subtle-foreground">{copy.empty.body}</p>
					<div className="flex flex-wrap justify-center gap-2">
						<Link
							className={cn(buttonVariants({ variant: "primary" }))}
							to="/vetting"
						>
							{copy.empty.backLabel}
						</Link>
					</div>
				</div>
			</div>
		)
	}

	const isImage = presentation === "image"

	return (
		<div
			className="flex flex-col gap-4"
			data-screen="attachment-viewer"
		>
			<p
				className="sr-only"
				role="status"
			>
				{mint.isPending
					? `Creating a secure link and opening ${fileName ?? "the document"}.`
					: null}
			</p>

			{/* Header — breadcrumb, title, and the primary action live on one row
			    so the reader is not scrolling to reach Download. */}
			<div className="flex flex-col gap-2">
				<Breadcrumb>
					{backTo ? (
						<BreadcrumbItem>
							<Link
								to={backTo.replace(/^\/panel/, "")}
								className="hover:text-foreground underline-offset-2 hover:underline"
							>
								{backLabel ?? "Back"}
							</Link>
						</BreadcrumbItem>
					) : null}
					<BreadcrumbItem last>
						<BreadcrumbCurrent>Document</BreadcrumbCurrent>
					</BreadcrumbItem>
				</Breadcrumb>

				<div className="flex flex-wrap items-start justify-between gap-3">
					<div className="flex min-w-0 items-center gap-3">
						<span
							className="bg-subtle text-primary flex size-10 shrink-0 items-center justify-center rounded-lg"
							aria-hidden="true"
						>
							{isImage ? (
								<ImageIcon className="size-5" />
							) : (
								<FileTextIcon className="size-5" />
							)}
						</span>
						<div className="min-w-0">
							<h1 className="text-page-title text-foreground truncate">{fileName}</h1>
							<p className="text-meta text-muted tabular-nums">
								{presentation === null
									? contentType
									: labelForPresentation(presentation)}
								{sizeBytes === undefined ? "" : ` · ${formatBytes(sizeBytes)}`}
							</p>
						</div>
					</div>
					<div className="flex shrink-0 flex-wrap gap-2">
						<Button
							type="button"
							variant="primary"
							disabled={save.isPending}
							onClick={download}
						>
							<DownloadIcon
								className="size-4 shrink-0"
								aria-hidden="true"
							/>
							{save.isPending ? "Getting a link…" : "Download"}
						</Button>
					</div>
				</div>
			</div>

			<div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
				<div className="flex flex-col gap-3">
					{/* Stage — the image sits on a neutral dark canvas so it reads
					    like a viewer, not a card in the page. Zoom controls float
					    at the bottom so nothing above the image competes with it. */}
					<figure className="rounded-card border-border relative flex h-[32rem] items-center justify-center overflow-hidden border bg-neutral-900 dark:bg-black">
						<div
							ref={stageRef}
							className={cn(
								"flex size-full items-center justify-center overflow-hidden select-none",
								isImage && signedUrl
									? zoom > 100
										? dragRef.current
											? "cursor-grabbing"
											: "cursor-grab"
										: "cursor-default"
									: undefined,
							)}
							onPointerDown={event => {
								if (!isImage || !signedUrl || zoom <= 100) return
								event.currentTarget.setPointerCapture(event.pointerId)
								dragRef.current = {
									startX: event.clientX,
									startY: event.clientY,
									originX: pan.x,
									originY: pan.y,
								}
							}}
							onPointerMove={event => {
								if (!dragRef.current) return
								const { startX, startY, originX, originY } = dragRef.current
								setPan(
									clampPan(
										{
											x: originX + (event.clientX - startX),
											y: originY + (event.clientY - startY),
										},
										zoom,
									),
								)
							}}
							onPointerUp={event => {
								if (!dragRef.current) return
								event.currentTarget.releasePointerCapture(event.pointerId)
								dragRef.current = null
							}}
							onPointerCancel={() => {
								dragRef.current = null
							}}
							onDoubleClick={() => {
								if (!isImage || !signedUrl) return
								if (zoom === 100) {
									setZoom(200)
								} else {
									setZoom(100)
									setPan({ x: 0, y: 0 })
								}
							}}
						>
							{mint.isPending && !signedUrl ? (
								<Skeleton className="h-64 w-full max-w-lg bg-white/10" />
							) : signedUrl && isImage ? (
								<img
									ref={imgRef}
									src={signedUrl}
									alt={fileName}
									draggable={false}
									className="max-h-full max-w-full origin-center rounded shadow-2xl transition-transform duration-100 ease-out"
									style={{
										transform: `translate(${String(pan.x)}px, ${String(pan.y)}px) scale(${String(zoom / 100)})`,
									}}
								/>
							) : signedUrl && presentation === "pdf" ? (
								<StageMessage
									title="PDFs open in a new browser tab"
									body="Your browser's own PDF viewer has page navigation, zoom and save."
									action={
										<a
											className={buttonVariants({ variant: "primary" })}
											href={signedUrl}
											target="_blank"
											rel="noopener noreferrer"
										>
											Open in a new tab
										</a>
									}
								/>
							) : signedUrl && presentation === "download_only" ? (
								<StageMessage
									title="Download to view"
									body="Word documents are not previewed here. Save the file and open it in Word or Google Docs."
									action={
										<Button
											type="button"
											variant="primary"
											disabled={save.isPending}
											onClick={download}
										>
											{save.isPending ? "Getting a link…" : "Download"}
										</Button>
									}
								/>
							) : (
								<p className="text-white/70">{copy.loading}</p>
							)}
						</div>

						{isImage && signedUrl ? (
							<div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
								<div className="bg-background/95 border-border pointer-events-auto flex items-center gap-1 rounded-full border p-1 shadow-lg backdrop-blur">
									<Button
										type="button"
										variant="quiet"
										size="icon"
										aria-label="Zoom out"
										disabled={zoom <= ZOOM_MIN}
										className="hover:bg-surface-hover cursor-pointer rounded-full"
										onClick={() => {
											setZoom(current => {
												const next = Math.max(ZOOM_MIN, current - ZOOM_STEP)
												if (next === 100) setPan({ x: 0, y: 0 })
												else setPan(previous => clampPan(previous, next))
												return next
											})
										}}
									>
										<MinusIcon
											className="size-4"
											aria-hidden="true"
										/>
									</Button>
									<span className="w-12 text-center text-xs tabular-nums">
										{zoom}%
									</span>
									<Button
										type="button"
										variant="quiet"
										size="icon"
										aria-label="Zoom in"
										disabled={zoom >= ZOOM_MAX}
										className="hover:bg-surface-hover cursor-pointer rounded-full"
										onClick={() => {
											setZoom(current =>
												Math.min(ZOOM_MAX, current + ZOOM_STEP),
											)
										}}
									>
										<PlusIcon
											className="size-4"
											aria-hidden="true"
										/>
									</Button>
								</div>
							</div>
						) : null}
					</figure>

					{saved ? (
						<div className="rounded-card border-status-answered-border bg-status-answered-bg flex items-start gap-3 border p-3">
							<CheckIcon
								className="size-5 shrink-0"
								aria-hidden="true"
							/>
							<div>
								<h3 className="text-card-title text-foreground">
									Saved to your Downloads folder
								</h3>
								<p className="text-description text-subtle-foreground">
									{fileName}
									{sizeBytes === undefined ? "" : ` · ${formatBytes(sizeBytes)}`}.
								</p>
							</div>
						</div>
					) : null}
				</div>

				{/* Rail — compact meta and the link countdown. On narrow screens
				    the grid collapses and the rail flows under the stage. */}
				<aside className="flex flex-col gap-3">
					<div className="rounded-card border-border bg-card flex flex-col gap-3 border p-4">
						<div className="flex items-center gap-2">
							<ClockIcon
								className="size-4 shrink-0"
								aria-hidden="true"
							/>
							<h2 className="text-card-title text-foreground">
								{copy.linkCard.title}
							</h2>
						</div>
						<div
							className={`track ${spent ? "is-spent" : trackFraction < 0.2 ? "is-warning" : ""}`.trim()}
							aria-hidden="true"
						>
							<i style={{ width: `${String(Math.round(trackFraction * 100))}%` }} />
						</div>
						<p className="text-meta text-muted">
							{spent
								? copy.linkCard.spent
								: expiresAt
									? `${copy.linkCard.expiresPrefix}${formatCountdown(remainingSeconds)}`
									: copy.linkCard.notOpened}
						</p>
						<Button
							type="button"
							variant={spent ? "primary" : "secondary"}
							disabled={mint.isPending}
							onClick={renew}
						>
							{mint.isPending
								? "Getting a link…"
								: spent
									? "Get a new link"
									: "Renew the link"}
						</Button>
						<p className="text-meta text-muted">{copy.linkCard.hint}</p>
					</div>

					{mint.isError && isAccessDenied(mint.error) ? (
						<div
							className="rounded-card border-status-breach-border bg-status-breach-bg flex items-start gap-3 border p-3"
							role="alert"
						>
							<TriangleAlertIcon
								className="size-5 shrink-0"
								aria-hidden="true"
							/>
							<div>
								<h3 className="text-card-title text-foreground">
									{copy.deny.title}
								</h3>
								<p className="text-description text-subtle-foreground">
									{copy.deny.body}
								</p>
								<div className="mt-1 flex flex-wrap gap-2">
									<Link
										className={cn(buttonVariants({ variant: "secondary" }))}
										to="/vetting"
									>
										{copy.deny.backLabel}
									</Link>
								</div>
							</div>
						</div>
					) : null}
					{mint.isError && !isAccessDenied(mint.error) ? (
						<div
							className="rounded-card border-status-breach-border bg-status-breach-bg flex items-start gap-3 border p-3"
							role="alert"
						>
							<TriangleAlertIcon
								className="size-5 shrink-0"
								aria-hidden="true"
							/>
							<div>
								<h3 className="text-card-title text-foreground">
									{toDisplayable(mint.error).title}
								</h3>
								<p className="text-description text-subtle-foreground">
									{toDisplayable(mint.error).message}
								</p>
								<div className="mt-1 flex flex-wrap gap-2">
									<button
										type="button"
										className={cn(buttonVariants({ variant: "secondary" }))}
										onClick={renew}
									>
										Try again
									</button>
								</div>
							</div>
						</div>
					) : null}
				</aside>
			</div>
		</div>
	)
}

function StageMessage({ title, body, action }: { title: string; body: string; action: ReactNode }) {
	return (
		<div className="flex max-w-md flex-col items-center gap-3 text-center text-white">
			<h2 className="text-card-title">{title}</h2>
			<p className="text-description text-white/70">{body}</p>
			<div>{action}</div>
		</div>
	)
}
